import { NgClass, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivityLocationFacade } from '@curbnturf/activity/src/lib/+state/activity.facade';
import { LoginFirstModalComponent } from '@curbnturf/auth-modal/src/lib/login-first-modal/login-first-modal.component';
import { BoondockFacade } from '@curbnturf/boondock-listing';
import { DonationModalPage } from '@curbnturf/credit-card/src/lib/donation-modal-page/donation-modal-page';
import {
  ActivityLocation,
  Boondock,
  IBoondockPoint,
  IRoutePoint,
  LatLon,
  Listing,
  ListingPoint,
  RoutePoint,
  User,
} from '@curbnturf/entities';
import { ListingFacade } from '@curbnturf/listing';
import { NavigateToHelper } from '@curbnturf/map/src/lib/geocoding/navigate-to-helper.service';
import { RatingBoondockModalControllerComponent, RatingPoiPageComponent } from '@curbnturf/rating-components';
import { TripPlannerFacade } from '@curbnturf/trip-planner/src/lib/+state/trip-planner.facade';
import { UserFacade } from '@curbnturf/user/src/lib/+state/user.facade';
import { IonicModule, ModalController } from '@ionic/angular';
import { Subscription } from 'rxjs';

@Component({
  standalone: true,
  selector: 'curbnturf-save-favorite-mobile',
  templateUrl: './save-favorite-mobile.component.html',
  styleUrls: ['./save-favorite-mobile.component.scss'],
  imports: [
    DonationModalPage,
    IonicModule,
    LoginFirstModalComponent,
    NgClass,
    NgIf,
    RatingBoondockModalControllerComponent,
    RatingPoiPageComponent,
  ],
})
export class SaveFavoriteMobileComponent implements OnDestroy, OnInit {
  @Input() point?: ListingPoint;
  @Input() boondockPoint?: IBoondockPoint;
  @Input() poi?: ActivityLocation;
  @Input() icon: boolean = false;
  @Input() size: string = 'medium';
  @Input() right: boolean = false;

  get favorited(): boolean {
    if (this.point) {
      return !!this.user?.listingFavorites?.find((favorite) => favorite.listingId === this.point?.id);
    } else if (this.boondockPoint) {
      return !!this.user?.boondockFavorites?.find((favorite) => favorite.boondockId === this.boondockPoint?.id);
    } else if (this.poi) {
      return !!this.user?.poiFavorites?.find((favorite) => favorite.poiId === this.poi?.id);
    }

    return false;
  }

  user: User;
  boondockRateModalOpen: boolean = false;
  boondockDonateModalOpen: boolean = false;
  poiRateModalOpen: boolean = false;
  poiDonateModalOpen: boolean = false;

  rateBoondockModalId: string = `rate-boondock-modal-undefined`;
  ratePoiModalId: string = `rate-poi-modal-undefined`;
  donateBoondockModalId: string = `donate-boondock-modal-undefined`;
  donatePoiModalId: string = `donate-poi-modal-undefined`;
  get addedToTrip(): boolean {
    if (this.trip.length > 0 && this.point?.id) {
      const listingFound = this.trip.find((el) => {
        if (el.isListing && this.point?.listing?.id && el.listing?.id) {
          return el.listing?.id === this.point.id;
        }
        return undefined;
      });
      const poiFound = this.trip.find((el) => {
        if (el.isPoi && this.point?.id && el.poi?.id) {
          return el.poi?.id === this.point.id;
        }
        return undefined;
      });
      const boondockFound = this.trip.find((el) => {
        if (el.isBoondock && this.boondockPoint?.id && el.boondock?.id) {
          return el.boondock?.id === this.boondockPoint.id;
        }
        return undefined;
      });

      if (listingFound || poiFound || boondockFound) {
        return true;
      }
    }
    return false;
  }

  private trip: IRoutePoint[] = [];
  private subscriptions: Subscription[] = [];

  constructor(
    private poiFacade: ActivityLocationFacade,
    private boondockFacade: BoondockFacade,
    private cd: ChangeDetectorRef,
    private modalController: ModalController,
    private navigateToHelper: NavigateToHelper,
    private listingFacade: ListingFacade,
    private tripPlannerFacade: TripPlannerFacade,
    private userFacade: UserFacade,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.userFacade.currentUser$.subscribe((user) => {
        this.user = user;
        this.cd.detectChanges();
      }),
      this.tripPlannerFacade.trip$.subscribe((trip) => {
        if (trip && trip.length > 0) {
          this.trip = trip;
        } else {
          this.trip = [];
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
    });
  }

  async openModalSave() {
    this.openLoginModal('To save you must be logged in.');
  }

  openModalRate() {
    this.openLoginModal('To rate you must be logged in.');
  }

  openModalDonate() {
    this.openLoginModal('To donate you must be logged in.');
  }

  favorite() {
    if (this.point?.id) {
      this.listingFacade.favorite(this.point);
    } else if (this.boondockPoint?.id) {
      this.boondockFacade.favorite(this.boondockPoint);
    } else if (this.poi?.id) {
      this.poiFacade.favorite(this.poi);
    }
  }

  unfavorite() {
    if (this.point?.id) {
      this.listingFacade.unfavorite(this.point);
    } else if (this.boondockPoint?.id) {
      this.boondockFacade.unfavorite(this.boondockPoint);
    } else if (this.poi?.id) {
      this.poiFacade.unfavorite(this.poi);
    }
  }

  // this is only for boondocking and activities as directions to listing is hidden until reservation is confirmed
  navigate() {
    if (this.boondockPoint) {
      window.open(this.navigateToHelper.makeNavigationLinkFromBoondockPoint(this.boondockPoint));
    } else if (this.poi) {
      window.open(this.navigateToHelper.makeLinkFromActivity(this.poi));
    }
  }

  // this is only for boondocking and activities as you can't rate a listing until you have stayed there
  async rate() {
    if (this.boondockPoint) {
      this.boondockFacade.select(this.boondockPoint.id);
      const modal = await this.modalController.create({
        component: RatingBoondockModalControllerComponent,
        componentProps: {
          boondockId: this.boondockPoint.id,
        },
      });
      await modal.present();
    } else if (this.poi?.id) {
      this.poiFacade.load(this.poi.id);
      const modal = await this.modalController.create({
        component: RatingPoiPageComponent,
        componentProps: {
          poi: this.poi,
        },
      });
      await modal.present();
    }
  }

  async donate() {
    // todo: this.point probably need so to change
    if (this.boondockPoint) {
      const modal = await this.modalController.create({
        component: DonationModalPage,
        componentProps: {
          boondockSite: this.boondockPoint,
        },
      });
      await modal.present();
    } else if (this.poi?.id) {
      const modal = await this.modalController.create({
        component: DonationModalPage,
        componentProps: {
          poi: this.poi,
        },
      });
      await modal.present();
    }
  }

  @HostListener('click', ['$event'])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onClick(event: any): void {
    event.stopPropagation();
  }

  addToTrip() {
    const point = new RoutePoint({ isActive: true, coordinates: { lat: 0, lon: 0 } });

    if (this.poi) {
      point.poi = this.poi;
      point.isPoi = true;
      point.coordinates = new LatLon({
        lat: this.poi.coordinates.lat,
        lon: this.poi.coordinates.lon,
      });
    } else if (this.boondockPoint) {
      point.isBoondock = true;
      point.coordinates = new LatLon({
        lat: this.boondockPoint.coordinates.lat,
        lon: this.boondockPoint.coordinates.lon,
      });
      point.boondock = new Boondock({
        id: this.boondockPoint.id || 0,
        name: this.boondockPoint.name || '',
        address: this.boondockPoint.address,
        photos: this.boondockPoint.photos || [],
        coordinates: this.boondockPoint.coordinates || new LatLon({ lat: 0, lon: 0 }),
        rating: this.boondockPoint.rating || 1,
        ratingCount: this.boondockPoint.ratingCount || 0,
      });
    } else if (this.point) {
      point.isListing = true;
      point.listing = this.point;
      point.coordinates = new LatLon({
        lat: this.point.coordinates?.lat || 0,
        lon: this.point.coordinates?.lon || 0,
      });
      point.listing = new Listing({
        id: this.point.id || 0,
        name: this.point.name || '',
        address: this.point.address,
        photos: this.point.photos || [],
        coordinates: this.point.coordinates || new LatLon({ lat: 0, lon: 0 }),
        rating: this.point.rating || 1,
        ratingCount: this.point.ratingCount || 0,
      });
    }

    this.tripPlannerFacade.addPoint(point);
  }

  removeFromTrip() {
    const foundPoint = this.trip.find(
      (el) =>
        el.listing?.id === this.point?.id || el.poi?.id === this.poi?.id || el.boondock?.id === this.boondockPoint?.id,
    );
    if (foundPoint) {
      this.tripPlannerFacade.removePoint(foundPoint);
    }
  }

  private async openLoginModal(text: string) {
    let redirect = '';
    if (this.point?.id) {
      redirect = `/listing/${this.point.id}`;
    } else if (this.boondockPoint?.id) {
      redirect = `/boondocking/${this.boondockPoint.id}`;
    } else if (this.poi?.id) {
      redirect = `${location?.pathname}`;
    }

    const modal = await this.modalController.create({
      component: LoginFirstModalComponent,
      componentProps: {
        text,
        redirect,
      },
      cssClass: 'confirm-modal',
    });
    await modal.present();
  }
}
